import React, { useState, useEffect, useMemo } from 'react';
import { IonIcon, useIonToast } from '@ionic/react';
import { close } from 'ionicons/icons';
import { useCreateVisit, useUpdateVisit } from '@/hooks/useVisits';
import { useFamilies } from '@/hooks/useFamilies';
import type { Visit, VisitType, VisitFormData } from '@/types/visit';
import { VISIT_TYPE_LABELS } from '@/utils/labels';
import SearchableSelect from '@/components/shared/SearchableSelect';

interface VisitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  visit?: Visit | null;
  familyId?: string;
  defaultVisitType?: VisitType;
}

const EMPTY_FORM: VisitFormData = {
  family_id: '',
  visit_date: new Date().toISOString().slice(0, 10),
  visit_type: 'pastorala',
  notes: null,
  has_special_need: false,
  special_need_type: null,
  special_need_notes: null,
};

const VisitModal: React.FC<VisitModalProps> = ({ isOpen, onClose, onSuccess, visit, familyId, defaultVisitType }) => {
  const [presentToast] = useIonToast();
  const createMut = useCreateVisit();
  const updateMut = useUpdateVisit();
  const { data: familiesData } = useFamilies({ page_size: 500 });
  const familiesList = familiesData?.data ?? [];
  const familyOptions = useMemo(() =>
    familiesList.map((f) => ({ value: f.id, label: f.display_name ?? f.name })),
    [familiesList],
  );

  const isEdit = !!visit;

  const [formData, setFormData] = useState<VisitFormData>(EMPTY_FORM);

  useEffect(() => {
    if (isOpen) {
      if (visit) {
        setFormData({
          family_id: visit.family_id,
          visit_date: visit.visit_date?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
          visit_type: visit.visit_type,
          notes: visit.notes,
          has_special_need: visit.has_special_need,
          special_need_type: visit.special_need_type,
          special_need_notes: visit.special_need_notes,
        });
      } else {
        setFormData({ ...EMPTY_FORM, family_id: familyId ?? '', visit_type: defaultVisitType ?? 'pastorala' });
      }
    }
  }, [isOpen, visit, familyId]);

  const handleSubmit = async () => {
    if (!formData.family_id) {
      void presentToast({ message: 'Selectează o familie.', duration: 2000, color: 'warning', position: 'bottom' });
      return;
    }
    if (!formData.visit_date) {
      void presentToast({ message: 'Data vizitei este obligatorie.', duration: 2000, color: 'warning', position: 'bottom' });
      return;
    }

    try {
      if (isEdit && visit) {
        await updateMut.mutateAsync({ id: visit.id, data: formData });
        void presentToast({ message: 'Vizita actualizată.', duration: 2000, color: 'success', position: 'bottom' });
      } else {
        await createMut.mutateAsync(formData);
        void presentToast({ message: 'Vizita înregistrată.', duration: 2000, color: 'success', position: 'bottom' });
      }
      onSuccess?.();
      onClose();
    } catch {
      void presentToast({ message: 'Eroare la salvare.', duration: 2000, color: 'danger', position: 'bottom' });
    }
  };

  if (!isOpen) return null;

  const isPending = createMut.isPending || updateMut.isPending;

  return (
    <div className="logout-overlay" onClick={onClose}>
      <div className="logout-dialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480, width: '90%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            {isEdit ? 'Editează Vizita' : 'Vizită Nouă'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <IonIcon icon={close} style={{ fontSize: 22, color: '#94a3b8' }} />
          </button>
        </div>

        {/* Family */}
        <div className="form-field" style={{ marginBottom: 16 }}>
          <label className="form-field__label">Familie *</label>
          <SearchableSelect
            options={familyOptions}
            value={formData.family_id || null}
            onChange={(val) => setFormData({ ...formData, family_id: val ?? '' })}
            placeholder="Caută familie..."
            emptyLabel="Selectează familia..."
            disabled={!!familyId}
          />
        </div>

        {/* Date */}
        <div className="form-field" style={{ marginBottom: 16 }}>
          <label className="form-field__label">Data vizitei *</label>
          <input
            className="form-input"
            type="date"
            value={formData.visit_date}
            onChange={(e) => setFormData({ ...formData, visit_date: e.target.value })}
          />
        </div>

        {/* Visit type */}
        <div className="form-field" style={{ marginBottom: 16 }}>
          <label className="form-field__label">Tip vizită</label>
          <select
            className="form-input"
            value={formData.visit_type}
            onChange={(e) => setFormData({ ...formData, visit_type: e.target.value as VisitType })}
          >
            {(Object.keys(VISIT_TYPE_LABELS) as VisitType[]).map((type) => (
              <option key={type} value={type}>{VISIT_TYPE_LABELS[type]}</option>
            ))}
          </select>
        </div>

        {/* Notes */}
        <div className="form-field" style={{ marginBottom: 16 }}>
          <label className="form-field__label">Notițe</label>
          <textarea
            className="form-input"
            value={formData.notes ?? ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value || null })}
            placeholder="Observații despre vizită..."
            rows={3}
            style={{ resize: 'vertical' }}
          />
        </div>

        {/* Special need toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: formData.has_special_need ? 12 : 24 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 500, color: '#475569', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={!!formData.has_special_need}
              onChange={(e) => setFormData({
                ...formData,
                has_special_need: e.target.checked,
                special_need_type: e.target.checked ? formData.special_need_type : null,
                special_need_notes: e.target.checked ? formData.special_need_notes : null,
              })}
              style={{ width: 16, height: 16 }}
            />
            Nevoie specială
          </label>
        </div>

        {formData.has_special_need && (
          <>
            <div className="form-field" style={{ marginBottom: 16 }}>
              <label className="form-field__label">Tip nevoie</label>
              <input
                className="form-input"
                value={formData.special_need_type ?? ''}
                onChange={(e) => setFormData({ ...formData, special_need_type: e.target.value || null })}
                placeholder="ex: medicală, financiară..."
              />
            </div>
            <div className="form-field" style={{ marginBottom: 24 }}>
              <label className="form-field__label">Detalii nevoie</label>
              <textarea
                className="form-input"
                value={formData.special_need_notes ?? ''}
                onChange={(e) => setFormData({ ...formData, special_need_notes: e.target.value || null })}
                placeholder="Detalii suplimentare..."
                rows={2}
                style={{ resize: 'vertical' }}
              />
            </div>
          </>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="form-btn form-btn--secondary" onClick={onClose}>
            Anulează
          </button>
          <button className="form-btn form-btn--primary" onClick={handleSubmit} disabled={isPending}>
            {isPending && <span className="login-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />}
            {isEdit ? 'Salvează' : 'Înregistrează'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VisitModal;
